'use client'

import { ArrowRight, Check, ChevronDown, Copy, Mail } from 'lucide-react'
import { motion, useScroll, useTransform } from 'motion/react'
import Link from 'next/link'
import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import { emailLink, routePaths } from '@/shared/config/site'

const primaryCtaLabel = 'Contact me'

const transformationSteps = [
  {
    body: 'Send the repo, the branch, the bug, and the outcome that matters. I start where the contradictions cluster.',
    mobileBody: 'Repo, branch, bug, outcome. I start at the contradiction.',
    title: 'Give me the ugly path',
  },
  {
    body: 'Autistic focus is useful here: I trace state, data, prompts, deploys, and human assumptions until the owner can stop guessing.',
    mobileBody: 'I trace the pattern across state, data, prompts, and deploys.',
    title: 'I lock onto the pattern',
  },
  {
    body: 'You get commits, tests, deployment notes, and code your team can explain after the panic leaves the room.',
    mobileBody: 'Commits, tests, notes, and code the team can explain.',
    title: 'We ship the boring fix',
  },
]

const includedItems = [
  'Autistic pattern review across folder structure, state, data, failure paths, and contradictory code.',
  'A blunt damage report written for humans: keep, cut, rebuild, fix first.',
  'Senior implementation across React, TypeScript, APIs, AI workflows, data, and deployment seams.',
  'Handoff notes with exact decisions, remaining risks, and next cuts.',
]

const comparisonRows = [
  {
    agency: 'Discovery before someone opens the repo',
    aiOnly: 'More code on top of the same confusion',
    criteria: 'First useful output',
    hire: 'A blunt map of what is broken and where to cut',
  },
  {
    agency: 'Tickets routed through layers',
    aiOnly: 'Local guesses from whatever context fits',
    criteria: 'Debugging style',
    hire: 'Autistic pattern lock across state, data, prompts, and deploys',
  },
  {
    agency: 'Greenfield builds with room for meetings',
    aiOnly: 'Fresh demos and shallow fixes',
    criteria: 'Best fit',
    hire: 'Ugly systems, AI slop, brittle UI, strange backend behavior',
  },
  {
    agency: 'Decks, standups, and consensus fog',
    aiOnly: 'Confident paragraphs',
    criteria: 'Communication',
    hire: 'Specific written tradeoffs with no softened risk language',
  },
]

const visualStrip = [
  {
    alt: 'Dark engineering desk with monitors, hardware boards, and amber task lights',
    caption: 'Find the contradiction the bot pasted over.',
    src: '/landing/software-rescue-workshop.webp',
  },
  {
    alt: 'Abstract code forensics panels suspended above a dark workbench',
    caption: 'Turn pattern recognition into a repair path.',
    src: '/landing/code-forensics-workbench.webp',
  },
  {
    alt: 'Amber-lit server corridor and deployment bench in a dark data center',
    caption: 'Force the demo to meet production.',
    src: '/landing/production-hardening-bay.webp',
  },
  {
    alt: 'Dark handoff table with abstract architecture diagrams and repair notes',
    caption: 'Leave the next engineer a map.',
    src: '/landing/engineering-handoff-table.webp',
  },
]

const highlightedWorkItems = [
  {
    careerId: 'blprnt',
    company: 'blprnt',
    id: 'blprnt',
    role: 'Founder / Lead Engineer',
    stack: ['Rust', 'TypeScript', 'React', 'SurrealDB', 'Docker'],
    summary:
      'Built a local AI execution runtime for autonomous teams: Rust coordinator, durable state, multi-provider model routing, and a React/TypeScript operating surface.',
    outcomes: [
      'Scheduled agents through manual triggers, heartbeats, issue assignment, and @mention handoffs.',
      'Packaged the product as a single-command install with cross-platform binaries and Docker deploys.',
      'Kept durable run, employee, and issue state inside a modular Rust workspace backed by SurrealDB.',
    ],
  },
  {
    careerId: 'aws',
    company: 'Amazon Web Services',
    id: 'aws',
    role: 'Software Engineer Level 6',
    stack: ['React', 'TypeScript', 'Webpack', 'Java'],
    summary:
      'Led frontend modernization inside AWS, moving a legacy Java UI toward React and TypeScript with tighter delivery, accessibility, and review standards.',
    outcomes: [
      'Improved maintainability and accessibility through a React + TypeScript migration.',
      'Modernized CI/CD with Webpack, TypeScript, and AWS CodePipeline.',
      'Mentored engineers through code review and technical guidance.',
    ],
  },
  {
    company: 'Trading Systems & Consulting',
    id: 'latency-systems',
    role: 'Consultant / Systems Engineer',
    stack: ['Rust', 'React', 'TypeScript', 'Node.js', 'PostgreSQL', 'Docker'],
    summary:
      'Delivered low-latency Rust services, real-time ingestion pipelines, and data-dense React workflows across fintech, ecommerce, and SaaS products with hard failure boundaries.',
    outcomes: [
      'Cut end-to-end latency by 50 percent by tightening IPC payloads and backend workflows.',
      'Built real-time Rust ingestion pipelines with async architecture, Docker, PostgreSQL, and SurrealDB.',
      'Developed React frontends and Node sidecars for complex operator-facing products.',
    ],
  },
]

export function HomePage() {
  const isMobileHeaderHidden = useMobileHeaderAutoHide()
  const workItems = highlightedWorkItems
  const services = [
    {
      bestFor: 'You have the demo. You need autistic focus on the parts AI skipped.',
      description:
        'Full-stack implementation for the app AI helped start: features, services, integrations, data flow, and the production seams the bot keeps smearing over.',
      outputs: ['Critical path shipped', 'Production seams fixed', 'Readable code'],
      title: 'Software Engineering',
    },
    {
      bestFor: 'The interface looks fine until a user behaves like a user.',
      description:
        'React, TypeScript, forms, auth screens, state cleanup, SSR, and UI behavior traced by someone who has to chase broken interactions to ground.',
      outputs: ['State traced', 'Forms hardened', 'SSR checked'],
      title: 'Frontend Engineering',
    },
    {
      bestFor: 'You need the unfiltered read before another week disappears.',
      description:
        'A direct read on what you have, what is fake, what is dangerous, and what deserves attention before you lose another week to an autocomplete box.',
      outputs: ['Damage map', 'Scope cuts', 'Build order'],
      title: 'Consulting',
    },
    {
      bestFor: 'Your AI app has too many loose ends for another prompt.',
      description:
        'Triage for the 80% app stuck in the same almost-fixed loop: duplicated state, haunted workflows, mystery files, broken assumptions, and code the team has stopped trying to explain.',
      outputs: ['Repo triage', 'Failure path fixes', 'Cleaner seams'],
      title: 'VibeCode Unfucking',
    },
    {
      bestFor: 'Your team needs AI leverage and an adult in the room.',
      description:
        'Practical sessions for teams using AI to build code they can read, test, recover, and explain to the next engineer.',
      outputs: ['Prompt boundaries', 'Review habits', 'Failure checks'],
      title: 'Effective AI Use Workshops',
    },
    {
      bestFor: 'The AI feature now has to behave like software.',
      description:
        'LLM workflows, RAG, MCP, model routing, agent runtimes, and tool-use systems with contracts, evals, and failure handling instead of haunted text boxes.',
      outputs: ['Tool contracts', 'Eval loops', 'Agent boundaries'],
      title: 'AI Systems and Agent Tooling',
    },
    {
      bestFor: 'You can feel the shape is wrong and need someone to name it.',
      description:
        'A direct pass over structure, data flow, dependencies, failure modes, and generated architecture that looks engineered until a user touches it.',
      outputs: ['System map', 'Failure modes', 'Refactor order'],
      title: 'Architecture Review',
    },
    {
      bestFor: 'Localhost has been flattering your app.',
      description:
        'Slow pages, hydration bugs, weird routes, cache mistakes, SEO rendering, and deployment checks for the parts that only fail after users arrive.',
      outputs: ['Render fixes', 'Load cuts', 'Deploy checks'],
      title: 'Performance and SSR Repair',
    },
    {
      bestFor: 'You need engineering judgment before the app becomes folklore.',
      description:
        'Staff-level execution for teams who need someone to translate the mess, make hard calls, review code, and keep the build out of prompt archaeology.',
      outputs: ['Review standards', 'Delivery rhythm', 'Sharper ownership'],
      title: 'Technical Leadership',
    },
  ].slice(0, 6)

  useEffect(() => {
    document.title = 'Hire An Autist | Vitaliy'
  }, [])

  const scenes = [
    {
      align: 'left' as const,
      body: 'The app works until reality touches it: auth, users, payments, weird state, and the second click.',
      id: 'slop',
      image: '/landing/code-forensics-workbench.webp',
      note: 'AI got you a demo. My autistic focus earns its keep after the dopamine, where I stay with contradictions until I can name why the code fails.',
      title: 'What you brought me',
    },
    {
      align: 'right' as const,
      body: 'Pick the engagement that matches the failure path. Audit, repair, training, and technical leadership all end in written decisions and working code.',
      id: 'services',
      image: '/landing/production-hardening-bay.webp',
      note: 'You pay for obsessive attention to the part the team keeps talking around, then the discipline to make it boring.',
      title: 'What you hire me for',
    },
    {
      align: 'left' as const,
      body: 'Production damage, shipped systems, and code that had to keep running under real constraints.',
      id: 'work',
      image: '/landing/engineering-handoff-table.webp',
      note: 'Rust services, React migrations, realtime pipelines, AI runtimes, latency work, and the details my brain refuses to ignore.',
      title: 'Shit I shipped',
    },
  ]

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100">
      <a
        className="sr-only z-50 rounded-md bg-stone-50 px-4 py-3 text-sm font-medium text-stone-950 focus:not-sr-only focus:fixed focus:left-4 focus:top-4"
        href={`#${'content'}`}
      >
        {'Skip to content'}
      </a>

      <motion.header
        animate={{ y: isMobileHeaderHidden ? '-100%' : '0%' }}
        className="sticky top-0 z-50 border-b border-stone-800 bg-stone-950 will-change-transform"
        data-mobile-hidden={isMobileHeaderHidden ? 'true' : 'false'}
        transition={{ duration: 0.18, ease: 'easeOut' }}
      >
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-6 px-5 py-4 md:px-8">
          <a className="flex items-center gap-3 text-sm font-semibold text-stone-100" href={`#${'hero'}`}>
            <img
              alt=""
              aria-hidden="true"
              className="h-8 w-8 rounded-md border border-stone-700 object-cover"
              height="32"
              src={'/localization/autist-nav.webp'}
              width="32"
            />
            {'Hire An Autist'}
          </a>

          <nav aria-label="Primary navigation" className="hidden items-center gap-6 md:flex">
            <a className="text-sm text-stone-200 hover:text-stone-50" href={`#${'slop'}`}>
              {{
                id: 'slop',
                label: 'The Mess',
              }?.label ?? 'The Mess'}
            </a>
            <a className="text-sm text-stone-200 hover:text-stone-50" href={`#${'services'}`}>
              {'Services'}
            </a>
            <a className="text-sm text-stone-200 hover:text-stone-50" href={`#${'work'}`}>
              {'Proof'}
            </a>
            <a className="text-sm text-stone-200 hover:text-stone-50" href={`#${'faq'}`}>
              {'Questions'}
            </a>
          </nav>

          <Link
            className="rounded-lg border border-stone-600 px-4 py-2 text-sm font-medium text-stone-100 hover:border-stone-400 hover:bg-stone-900"
            href={routePaths.contact}
          >
            {primaryCtaLabel}
          </Link>
        </div>
      </motion.header>

      <main id={'content'}>
        <HeroLanding emailHref={emailLink.href} />

        <ProcessParallaxSection id={'proof'} steps={transformationSteps} />

        {scenes.map((scene, index) => (
          <ParallaxScene key={scene.id} layer={index} {...scene} />
        ))}

        <ServiceSummarySection
          lead={
            'You have a repo full of plausible answers and no proof the product can survive users. I bring autistic pattern recognition, senior engineering judgment, and the patience to chase the weird path until the fix is clear.'
          }
          serviceViewLabel={'Services'}
          services={services}
          title={'Where autistic focus pays off'}
        />

        <DemoParallaxSection ctaLabel={primaryCtaLabel} />

        <section className="relative z-50 bg-stone-950 px-5 py-24 md:px-8">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 -top-40 h-40 bg-gradient-to-t from-stone-950 to-transparent"
          />
          <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-2">
            <BlurReveal>
              <h2 className="text-3xl font-semibold tracking-tight text-stone-50 md:text-5xl">What you get</h2>
              <p className="mt-5 text-base leading-7 text-stone-200 md:text-lg">
                No vague retainer fog. You get the autistic inspection first: what breaks, what matters, and which
                repair buys the most safety.
              </p>
            </BlurReveal>
            <ul className="grid gap-4 md:grid-cols-2">
              {includedItems.map((item, index) => (
                <BlurRevealListItem
                  className="rounded-lg border border-stone-800 bg-stone-900 p-5 text-sm leading-6 text-stone-200"
                  delay={index * 0.06}
                  key={item}
                >
                  {item}
                </BlurRevealListItem>
              ))}
            </ul>
          </div>
        </section>

        <section className="border-y border-stone-800 bg-stone-950 px-5 py-24 md:px-8">
          <div className="mx-auto max-w-6xl">
            <div className="grid gap-8 lg:grid-cols-2">
              <BlurReveal>
                <h2 className="text-3xl font-semibold tracking-tight text-stone-50 md:text-5xl">
                  Proof the autist can ship
                </h2>
                <p className="mt-5 text-base leading-7 text-stone-200 md:text-lg">
                  {
                    'I have shipped software for 19 years and spent the last 2 fixing vibecoded SaaS messes for founders. The useful part is the same: pattern recognition, low tolerance for sloppy abstractions, and enough focus to finish the repair.'
                  }
                </p>
              </BlurReveal>
              <dl className="grid gap-4 sm:grid-cols-2">
                {[
                  {
                    detail: 'Fintech, cloud, commerce, AI-heavy products, and code with real consequences.',
                    label: 'Shipping software',
                    value: '19 years',
                  },
                  {
                    detail: 'Model routing, RAG, MCP, vector databases, and agent workflows that need contracts.',
                    label: 'AI systems',
                    value: 'AI / ML',
                  },
                  {
                    detail: 'Rust backends, React interfaces, latency work, and data flow the team keeps hand-waving.',
                    label: 'Build range',
                    value: 'Rust + React',
                  },
                  {
                    detail: 'Founder, staff-level engineer, and IC who can make the call and write the code.',
                    label: 'Ownership',
                    value: 'Lead roles',
                  },
                ].map((point, index) => (
                  <BlurReveal className="border-t border-stone-800 pt-5" delay={index * 0.06} key={point.label}>
                    <dt className="text-sm text-stone-300">{point.label}</dt>
                    <dd className="mt-2 text-2xl font-semibold text-stone-50">{point.value}</dd>
                    <dd className="mt-2 text-sm leading-6 text-stone-200">{point.detail}</dd>
                  </BlurReveal>
                ))}
              </dl>
            </div>

            <div className="mt-16 grid gap-4 lg:grid-cols-3">
              {workItems.map((item, index) => (
                <BlurReveal delay={index * 0.08} key={item.id}>
                  <article className="rounded-lg border border-stone-800 bg-stone-900 p-5">
                    <p className="text-sm text-stone-300">{item.company}</p>
                    <h3 className="mt-2 text-lg font-semibold text-stone-50">{item.role}</h3>
                    <p className="mt-4 text-sm leading-6 text-stone-200">{item.summary}</p>
                    <ul className="mt-5 flex flex-wrap gap-x-3 gap-y-2 text-xs text-stone-300">
                      {item.stack.slice(0, 5).map((stackItem) => (
                        <li key={stackItem}>{stackItem}</li>
                      ))}
                    </ul>
                  </article>
                </BlurReveal>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-stone-950 px-5 py-24 md:px-8">
          <div className="mx-auto max-w-6xl">
            <div className="grid gap-8 lg:grid-cols-2">
              <BlurReveal>
                <h2 className="text-3xl font-semibold tracking-tight text-stone-50 md:text-5xl">
                  Why hire an autist instead of asking the bot again?
                </h2>
                <p className="mt-5 text-base leading-7 text-stone-200 md:text-lg">
                  The bot drafts from context. I check the parts it treats as disposable: four owners for one value,
                  auth that lies in one route, and tests that missed the bug.
                </p>
              </BlurReveal>
              <BlurReveal className="overflow-x-auto">
                <table className="w-full min-w-max border-collapse text-left text-sm">
                  <thead>
                    <tr className="border-b border-stone-800 text-stone-200">
                      <th className="py-4 pr-5 font-medium">Decision point</th>
                      <th className="px-5 py-4 font-medium text-stone-50">Hire An Autist</th>
                      <th className="px-5 py-4 font-medium">AI-only pass</th>
                      <th className="py-4 pl-5 font-medium">Typical agency</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonRows.map((row, index) => (
                      <BlurRevealTableRow
                        className="border-b border-stone-800 align-top"
                        delay={index * 0.05}
                        key={row.criteria}
                      >
                        <th className="py-5 pr-5 font-medium text-stone-50">{row.criteria}</th>
                        <td className="px-5 py-5 text-stone-200">{row.hire}</td>
                        <td className="px-5 py-5 text-stone-300">{row.aiOnly}</td>
                        <td className="py-5 pl-5 text-stone-300">{row.agency}</td>
                      </BlurRevealTableRow>
                    ))}
                  </tbody>
                </table>
              </BlurReveal>
            </div>
          </div>
        </section>

        <section className="border-y border-stone-800 bg-stone-900 px-5 py-24 md:px-8" id={'faq'}>
          <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-2">
            <BlurReveal>
              <h2 className="text-3xl font-semibold tracking-tight text-stone-50 md:text-5xl">{'Before you ask'}</h2>
              <p className="mt-5 text-base leading-7 text-stone-200 md:text-lg">
                {
                  'The questions founders ask after they send the repo and before they decide whether the blunt branding is a bit.'
                }
              </p>
            </BlurReveal>
            <div className="divide-y divide-stone-800 border-y border-stone-800">
              {[
                {
                  answer:
                    'Within a week in most cases. Send a clean brief and I can move sooner. A vague calendar for next quarter belongs with someone else.',
                  question: 'How fast can you start?',
                },
                {
                  answer:
                    'Then I say so. The damage report gives you the least stupid next move: fix, rebuild, cut scope, or bury it.',
                  question: 'What if my code is too far gone?',
                },
                {
                  answer:
                    'Yes. Short audits, fractional advisory, and fix-and-ship blocks work. The scope follows the failure path.',
                  question: 'Do you do retainers or fractional work?',
                },
                {
                  answer:
                    'Yes. I teach teams where AI helps, where it lies, and how to review generated code before it mutates the product.',
                  question: 'Can you teach my team to use AI without setting the codebase on fire?',
                },
                {
                  answer:
                    'The mess decides. Audits stay contained. Last-mile fixes scale with risk, ownership, and how much generated code we need to unwind. I quote numbers, not vibes.',
                  question: 'What does it cost?',
                },
                {
                  answer:
                    'Diagnosed. My autism shows up in the work: pattern lock, low tolerance for fuzzy logic, and long focus runs on problems other people avoid.',
                  question: 'Are you autistic?',
                },
              ].map((item, index) => (
                <BlurReveal delay={index * 0.04} key={item.question}>
                  <details className="group py-5">
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-5 text-lg font-semibold text-stone-50">
                      <span>{item.question}</span>
                      <ChevronDown className="h-5 w-5 shrink-0 text-stone-200 transition-transform duration-150 group-open:rotate-180" />
                    </summary>
                    <p className="mt-3 text-sm leading-6 text-stone-200">{item.answer}</p>
                  </details>
                </BlurReveal>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-stone-950 px-5 py-24 md:px-8">
          <div className="mx-auto max-w-6xl">
            <BlurReveal>
              <h2 className="max-w-3xl text-3xl font-semibold tracking-tight text-stone-50 md:text-5xl">
                You should see the repair path before you pay for it.
              </h2>
            </BlurReveal>
            <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {visualStrip.map((image, index) => (
                <BlurReveal delay={index * 0.06} key={image.src}>
                  <figure className="overflow-hidden rounded-lg border border-stone-800 bg-stone-900">
                    <img
                      alt={image.alt}
                      className="aspect-video w-full object-cover"
                      decoding="async"
                      height="864"
                      loading="lazy"
                      src={image.src}
                      width="1536"
                    />
                    <figcaption className="p-4 text-sm leading-6 text-stone-200">{image.caption}</figcaption>
                  </figure>
                </BlurReveal>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-stone-950 px-5 pb-24 pt-8 md:px-8" id={'contact'}>
          <BlurReveal className="mx-auto max-w-6xl rounded-lg border border-stone-800 bg-stone-900 p-6 md:p-10">
            <div className="grid gap-8 lg:grid-cols-2 lg:items-center">
              <div>
                <h2 className="text-3xl font-semibold tracking-tight text-stone-50 md:text-5xl">{'Contact me'}</h2>
                <p className="mt-5 max-w-2xl text-base leading-7 text-stone-200 md:text-lg">
                  Send the repo, the broken flow, or the AI-generated knot the team keeps avoiding. I will read it, name
                  the failure path, and tell you where my attention helps most.
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
                <Link
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-stone-50 px-5 py-3 text-sm font-semibold text-stone-950 hover:bg-amber-200"
                  href={routePaths.contact}
                >
                  {primaryCtaLabel}
                  <ArrowRight className="h-4 w-4" />
                </Link>
                {emailLink ? <EmailSplitAction href={emailLink.href} label={'Email the Autist'} tone="panel" /> : null}
              </div>
            </div>
          </BlurReveal>
        </section>
      </main>
    </div>
  )
}

function useMobileHeaderAutoHide() {
  const [isHidden, setIsHidden] = useState(false)

  useEffect(() => {
    const mediaQuery = typeof window.matchMedia === 'function' ? window.matchMedia('(max-width: 767px)') : null
    let isMobile = mediaQuery?.matches ?? window.innerWidth < 768
    let lastScrollY = Math.max(window.scrollY, 0)
    let upwardDistance = 0

    const syncViewport = () => {
      isMobile = mediaQuery?.matches ?? window.innerWidth < 768
      lastScrollY = Math.max(window.scrollY, 0)
      upwardDistance = 0

      if (!isMobile) {
        setIsHidden(false)
      }
    }

    const handleScroll = () => {
      const currentScrollY = Math.max(window.scrollY, 0)
      const delta = currentScrollY - lastScrollY

      if (!isMobile || currentScrollY < 72) {
        upwardDistance = 0
        lastScrollY = currentScrollY
        setIsHidden(false)
        return
      }

      if (delta > 3) {
        upwardDistance = 0
        setIsHidden(true)
      }

      if (delta < 0) {
        upwardDistance += Math.abs(delta)

        if (upwardDistance >= 44) {
          setIsHidden(false)
        }
      }

      lastScrollY = currentScrollY
    }

    syncViewport()
    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', syncViewport)
    mediaQuery?.addEventListener?.('change', syncViewport)

    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', syncViewport)
      mediaQuery?.removeEventListener?.('change', syncViewport)
    }
  }, [])

  return isHidden
}

type EmailSplitActionTone = 'default' | 'panel'

const emailSplitActionToneClasses: Record<EmailSplitActionTone, { copy: string; link: string }> = {
  default: {
    copy: 'border-stone-700 text-stone-100 hover:border-stone-500 hover:bg-stone-900',
    link: 'border-stone-700 text-stone-100 hover:border-stone-500 hover:bg-stone-900',
  },
  panel: {
    copy: 'border-stone-700 text-stone-100 hover:border-stone-500 hover:bg-stone-950',
    link: 'border-stone-700 text-stone-100 hover:border-stone-500 hover:bg-stone-950',
  },
}

type EmailSplitActionProps = {
  href: string
  label: string
  tone?: EmailSplitActionTone
}

function EmailSplitAction({ href, label, tone = 'default' }: EmailSplitActionProps) {
  const [copied, setCopied] = useState(false)
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const emailAddress = decodeURIComponent(href.replace(/^mailto:/, '').split('?')[0])
  const copyButtonLabel = copied ? 'Email address copied' : 'Copy email address'
  const toneClasses = emailSplitActionToneClasses[tone]

  useEffect(() => {
    return () => {
      if (resetTimerRef.current !== null) {
        clearTimeout(resetTimerRef.current)
      }
    }
  }, [])

  async function handleCopyClick() {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(emailAddress)
      } else {
        copyEmailAddressWithFallback(emailAddress)
      }

      setCopied(true)

      if (resetTimerRef.current !== null) {
        clearTimeout(resetTimerRef.current)
      }

      resetTimerRef.current = setTimeout(() => setCopied(false), 1600)
    } catch {
      setCopied(false)
    }
  }

  return (
    <div className="inline-flex items-stretch">
      <a
        className={`inline-flex flex-1 items-center justify-center gap-2 rounded-l-lg rounded-r-none border border-r-0 px-5 py-3 text-sm font-semibold transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/70 ${toneClasses.link}`}
        href={href}
      >
        <Mail className="h-4 w-4" />
        {label}
      </a>
      <button
        aria-label={copyButtonLabel}
        className={`inline-flex shrink-0 items-center justify-center rounded-l-none rounded-r-lg border border-l px-3 py-3 text-sm font-semibold transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/70 ${toneClasses.copy}`}
        onClick={handleCopyClick}
        title={copyButtonLabel}
        type="button"
      >
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  )
}

function copyEmailAddressWithFallback(emailAddress: string) {
  if (typeof document === 'undefined') {
    throw new Error('Document is unavailable')
  }

  const textarea = document.createElement('textarea')
  textarea.value = emailAddress
  textarea.setAttribute('readonly', '')
  textarea.style.left = '-9999px'
  textarea.style.position = 'fixed'
  textarea.style.top = '0'
  document.body.append(textarea)
  textarea.select()

  const copied = document.execCommand('copy')
  textarea.remove()

  if (!copied) {
    throw new Error('Copy command failed')
  }
}

type BlurRevealProps = {
  children: ReactNode
  className?: string
  delay?: number
}

function BlurReveal({ children, className, delay = 0 }: BlurRevealProps) {
  return (
    <motion.div
      className={className}
      initial={{ filter: 'blur(22px)', opacity: 0, y: 34 }}
      transition={{
        delay,
        duration: 0.7,
        ease: 'easeOut',
      }}
      viewport={{ amount: 0.22, once: true }}
      whileInView={{ filter: 'blur(0px)', opacity: 1, y: 0 }}
    >
      {children}
    </motion.div>
  )
}

function BlurRevealListItem({ children, className, delay = 0 }: BlurRevealProps) {
  return (
    <motion.li
      className={className}
      initial={{ filter: 'blur(22px)', opacity: 0, y: 34 }}
      transition={{
        delay,
        duration: 0.7,
        ease: 'easeOut',
      }}
      viewport={{ amount: 0.22, once: true }}
      whileInView={{ filter: 'blur(0px)', opacity: 1, y: 0 }}
    >
      {children}
    </motion.li>
  )
}

function BlurRevealTableRow({ children, className, delay = 0 }: BlurRevealProps) {
  return (
    <motion.tr
      className={className}
      initial={{ filter: 'blur(18px)', opacity: 0, y: 18 }}
      transition={{
        delay,
        duration: 0.55,
        ease: 'easeOut',
      }}
      viewport={{ amount: 0.28, once: true }}
      whileInView={{ filter: 'blur(0px)', opacity: 1, y: 0 }}
    >
      {children}
    </motion.tr>
  )
}

type HeroLandingProps = {
  emailHref?: string
}

function HeroLanding({ emailHref }: HeroLandingProps) {
  const ref = useRef<HTMLElement | null>(null)
  const { scrollYProgress } = useScroll({ offset: ['start start', 'end start'], target: ref })
  const imageX = useTransform(scrollYProgress, [0, 1], [-40, 44])
  const imageY = useTransform(scrollYProgress, [0, 1], [-160, 190])
  const imageScale = useTransform(scrollYProgress, [0, 1], [1.22, 1.04])
  const imageFocus = useTransform(
    scrollYProgress,
    [0, 0.68, 1],
    ['blur(0px) brightness(1.08)', 'blur(0px) brightness(0.92)', 'blur(8px) brightness(0.72)'],
  )
  const copyOpacity = useTransform(scrollYProgress, [0, 0.78, 1], [1, 1, 0.56])
  const copyFocus = useTransform(scrollYProgress, [0, 0.82, 1], ['blur(0px)', 'blur(0px)', 'blur(6px)'])

  return (
    <section className="relative isolate z-0 bg-stone-950" id={'hero'} ref={ref}>
      <div className="sticky top-0 min-h-screen overflow-hidden">
        <motion.img
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
          decoding="async"
          fetchPriority="high"
          height="864"
          loading="eager"
          src="/landing/software-rescue-workshop.webp"
          style={{ filter: imageFocus, scale: imageScale, x: imageX, y: imageY }}
          width="1536"
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-950 via-stone-950/80 to-stone-950/10" />
        <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-stone-950 to-transparent" />

        <motion.div
          className="relative z-10 mx-auto flex min-h-screen max-w-6xl items-center px-5 py-24 md:px-8"
          style={{ filter: copyFocus, opacity: copyOpacity }}
        >
          <div className="max-w-3xl">
            <p className="max-w-xl text-lg leading-8 text-stone-200">
              {'I will audit, review, and fix your vibecoded shitshow.'}
            </p>
            <h1 className="mt-6 text-6xl font-semibold tracking-tight text-stone-50 md:text-8xl">
              {'Deploy The Autist'}
            </h1>
            <p className="mt-7 max-w-2xl text-xl leading-8 text-stone-200 md:text-2xl">
              {'Highly weaponized, military-grade autism solving your engineering challenges.'}
            </p>

            <div className="mt-10 flex flex-col gap-3 sm:flex-row">
              <Link
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-stone-50 px-5 py-3 text-sm font-semibold text-stone-950 hover:bg-amber-200"
                href={routePaths.contact}
              >
                {primaryCtaLabel}
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                className="inline-flex items-center justify-center rounded-lg border border-stone-600 px-5 py-3 text-sm font-semibold text-stone-100 hover:border-stone-400 hover:bg-stone-900"
                href={routePaths.service}
              >
                {'Services'}
              </Link>
              {emailHref ? <EmailSplitAction href={emailHref} label={'Email the Autist'} /> : null}
            </div>

            <dl className="mt-12 grid max-w-2xl gap-5 border-t border-stone-700 pt-6 sm:grid-cols-3">
              {[
                {
                  detail: 'Across fintech, cloud, commerce, and AI-heavy product work.',
                  label: 'Shipping software',
                  value: '19 years',
                },
                {
                  detail: 'Multi-agent orchestration, model routing, Vector databases, RAG, and MCP.',
                  label: 'Systems focus',
                  value: 'AI / ML',
                },
                {
                  detail: 'High-performance backends paired with crisp product-facing interfaces.',
                  label: 'Execution range',
                  value: 'Rust + React',
                },
                {
                  detail: 'Founder, staff-level engineer, and individual contributor.',
                  label: 'Delivery ownership',
                  value: 'Lead roles',
                },
              ]
                .slice(0, 3)
                .map((point) => (
                  <div key={point.label}>
                    <dt className="text-sm text-stone-300">{point.label}</dt>
                    <dd className="mt-1 text-xl font-semibold text-stone-50">{point.value}</dd>
                  </div>
                ))}
            </dl>
          </div>
        </motion.div>
      </div>
      <div aria-hidden="true" className="h-screen" />
    </section>
  )
}

type ProcessStep = {
  body: string
  mobileBody?: string
  title: string
}

type ProcessParallaxSectionProps = {
  id: string
  steps: ProcessStep[]
}

function ProcessParallaxSection({ id, steps }: ProcessParallaxSectionProps) {
  const ref = useRef<HTMLElement | null>(null)
  const { scrollYProgress } = useScroll({ offset: ['start end', 'end start'], target: ref })
  const imageX = useTransform(scrollYProgress, [0, 1], [72, -72])
  const imageY = useTransform(scrollYProgress, [0, 1], [-180, 190])
  const imageScale = useTransform(scrollYProgress, [0, 0.55, 1], [1.22, 1.08, 1.18])
  const imageFocus = useTransform(
    scrollYProgress,
    [0, 0.28, 0.9, 1],
    [
      'blur(16px) brightness(0.58)',
      'blur(0px) brightness(0.88)',
      'blur(0px) brightness(0.86)',
      'blur(8px) brightness(0.66)',
    ],
  )
  const contentOpacity = useTransform(scrollYProgress, [0, 0.22, 0.34, 1], [0, 0.82, 1, 1])
  const contentY = useTransform(scrollYProgress, [0, 0.34, 1], [34, 0, 0])
  const contentFocus = useTransform(scrollYProgress, [0, 0.34, 1], ['blur(18px)', 'blur(0px)', 'blur(0px)'])
  const wipeEdgeOpacity = useTransform(scrollYProgress, [0, 0.16, 0.34], [0, 1, 0])

  return (
    <section className="relative isolate z-10 -mt-[100vh] bg-stone-950" id={id} ref={ref}>
      <div className="sticky top-0 min-h-[100svh] overflow-hidden md:min-h-screen">
        <motion.img
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
          decoding="async"
          height="864"
          loading="lazy"
          src="/landing/engineering-handoff-table.webp"
          style={{ filter: imageFocus, scale: imageScale, x: imageX, y: imageY }}
          width="1536"
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-950 via-stone-950/80 to-stone-950/20" />
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-stone-950 to-transparent" />
        <motion.div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-stone-950 to-transparent"
          style={{ opacity: wipeEdgeOpacity }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 -top-40 h-40 bg-gradient-to-t from-stone-950 to-transparent"
        />

        <motion.div
          className="relative z-10 mx-auto flex min-h-[100svh] max-w-6xl items-center px-5 py-14 md:min-h-screen md:px-8 md:py-20"
          style={{ filter: contentFocus, opacity: contentOpacity, y: contentY }}
        >
          <div className="grid gap-6 md:gap-10 lg:grid-cols-2 lg:items-center">
            <div>
              <h2 className="text-3xl font-semibold tracking-tight text-stone-50 md:text-6xl">
                How the autist makes it sane
              </h2>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-stone-200 md:mt-6 md:text-lg md:leading-7">
                You hire me for the ugly systems work: sit with the broken thing, spot contradictions, delete noise, and
                make the code safe enough for users.
              </p>
            </div>

            <ol className="grid gap-3 md:gap-5">
              {steps.map((step, index) => (
                <li className="border-t border-stone-700 pt-3 md:pt-5" key={step.title}>
                  <p className="font-mono text-xs text-amber-300 md:text-sm">0{index + 1}</p>
                  <h3 className="mt-2 text-base font-semibold text-stone-50 md:mt-3 md:text-xl">{step.title}</h3>
                  <p className="mt-1 text-sm leading-5 text-stone-100 md:hidden">{step.mobileBody ?? step.body}</p>
                  <p className="mt-2 hidden text-sm leading-6 text-stone-100 md:block">{step.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </motion.div>
      </div>
      <div aria-hidden="true" className="h-[40svh] md:h-screen" />
    </section>
  )
}

type ServiceSummary = {
  description: string
  outputs: string[]
  title: string
}

type ServiceSummarySectionProps = {
  lead: string
  serviceViewLabel: string
  services: ServiceSummary[]
  title: string
}

function ServiceSummarySection({ lead, serviceViewLabel, services, title }: ServiceSummarySectionProps) {
  return (
    <section
      className="relative z-40 border-y border-stone-800 bg-stone-950 px-5 py-16 md:px-8 md:py-24"
      id="service-details"
    >
      <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-2 lg:items-start">
        <BlurReveal>
          <h2 className="text-3xl font-semibold tracking-tight text-stone-50 md:text-5xl">{title}</h2>
          <p className="mt-5 max-w-2xl text-base leading-7 text-stone-200 md:text-lg">{lead}</p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-stone-50 px-5 py-3 text-sm font-semibold text-stone-950 hover:bg-amber-200"
              href={routePaths.contact}
            >
              {primaryCtaLabel}
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              className="inline-flex items-center justify-center rounded-lg border border-stone-700 px-5 py-3 text-sm font-semibold text-stone-100 hover:border-stone-500 hover:bg-stone-900"
              href={routePaths.service}
            >
              {serviceViewLabel}
            </Link>
          </div>
        </BlurReveal>

        <div className="grid gap-4 sm:grid-cols-2">
          {services.map((service, index) => (
            <BlurReveal className="border-t border-stone-800 pt-4" delay={index * 0.04} key={service.title}>
              <article>
                <h3 className="text-base font-semibold text-stone-50">{service.title}</h3>
                <ul className="mt-3 space-y-1 text-xs leading-5 text-stone-200">
                  {service.outputs.slice(0, 2).map((output) => (
                    <li className="flex gap-2" key={output}>
                      <Check className="mt-0.5 h-3 w-3 shrink-0 text-amber-300" />
                      <span>{output}</span>
                    </li>
                  ))}
                </ul>
              </article>
            </BlurReveal>
          ))}
        </div>
      </div>
    </section>
  )
}

type DemoParallaxSectionProps = {
  ctaLabel: string
}

function DemoParallaxSection({ ctaLabel }: DemoParallaxSectionProps) {
  const ref = useRef<HTMLElement | null>(null)
  const { scrollYProgress } = useScroll({ offset: ['start end', 'end start'], target: ref })
  const imageX = useTransform(scrollYProgress, [0, 1], [54, -56])
  const imageY = useTransform(scrollYProgress, [0, 1], [-130, 150])
  const imageScale = useTransform(scrollYProgress, [0, 0.5, 1], [1.18, 1.05, 1.14])
  const imageFocus = useTransform(
    scrollYProgress,
    [0, 0.28, 0.88, 1],
    [
      'blur(14px) brightness(0.6)',
      'blur(0px) brightness(0.92)',
      'blur(0px) brightness(0.88)',
      'blur(8px) brightness(0.68)',
    ],
  )
  const contentOpacity = useTransform(scrollYProgress, [0, 0.8, 0.29, 1], [0, 0.82, 1, 1])
  const contentY = useTransform(scrollYProgress, [0, 0.29, 1], [38, 0, 0])
  const contentFocus = useTransform(scrollYProgress, [0, 0.29, 1], ['blur(18px)', 'blur(0px)', 'blur(0px)'])
  const wipeEdgeOpacity = useTransform(scrollYProgress, [0, 0.13, 0.29], [0, 1, 0])

  return (
    <section className="relative isolate z-40 bg-stone-900" ref={ref}>
      <div className="sticky top-0 min-h-screen overflow-hidden">
        <motion.img
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
          decoding="async"
          height="864"
          loading="lazy"
          src="/landing/production-hardening-bay.webp"
          style={{ filter: imageFocus, scale: imageScale, x: imageX, y: imageY }}
          width="1536"
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-950 via-stone-950/80 to-stone-950/20" />
        <div className="absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-stone-950 to-transparent" />
        <motion.div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-stone-950 to-transparent"
          style={{ opacity: wipeEdgeOpacity }}
        />

        <motion.div
          className="relative z-10 mx-auto flex min-h-screen max-w-6xl items-center px-5 py-20 md:px-8"
          style={{ filter: contentFocus, opacity: contentOpacity, y: contentY }}
        >
          <div className="max-w-2xl">
            <h2 className="text-4xl font-semibold tracking-tight text-stone-50 md:text-6xl">Contact me</h2>
            <p className="mt-5 text-base leading-7 text-stone-200 md:text-lg">
              Another prompt adds code. I find the contradiction.
            </p>
            <Link
              className="mt-8 inline-flex items-center justify-center gap-2 rounded-lg bg-amber-300 px-5 py-3 text-sm font-semibold text-stone-950 hover:bg-amber-200"
              href={routePaths.contact}
            >
              {ctaLabel}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </motion.div>
      </div>
      <div aria-hidden="true" className="h-screen" />
      <div aria-hidden="true" className="h-96 md:h-screen" />
    </section>
  )
}

type ParallaxSceneProps = {
  align: 'left' | 'right'
  body: string
  id: string
  image: string
  layer: number
  note: string
  title: string
}

function ParallaxScene({ align, body, id, image, layer, note, title }: ParallaxSceneProps) {
  const ref = useRef<HTMLElement | null>(null)
  const { scrollYProgress } = useScroll({ offset: ['start end', 'end start'], target: ref })
  const layerClass = layer === 2 ? 'z-40' : layer === 1 ? 'z-30' : 'z-20'
  const overlapClass = '-mt-[100vh]'
  const wipeEdgeOpacity = useTransform(scrollYProgress, [0, 0.16, 0.34], [0, 1, 0])
  const imageX = useTransform(scrollYProgress, [0, 1], align === 'right' ? [90, -80] : [-90, 80])
  const imageY = useTransform(scrollYProgress, [0, 1], [-260, 260])
  const imageScale = useTransform(scrollYProgress, [0, 0.5, 1], [1.28, 1.08, 1.24])
  const imageFocus = useTransform(
    scrollYProgress,
    [0, 0.3, 0.9, 1],
    [
      'blur(18px) brightness(0.58)',
      'blur(0px) brightness(0.92)',
      'blur(0px) brightness(0.9)',
      'blur(10px) brightness(0.66)',
    ],
  )
  const copyX = useTransform(scrollYProgress, [0, 0.34, 1], align === 'right' ? [120, 0, 0] : [-120, 0, 0])
  const copyOpacity = useTransform(scrollYProgress, [0, 0.24, 0.34, 1], [0, 0.82, 1, 1])
  const copyScale = useTransform(scrollYProgress, [0, 0.34, 1], [0.92, 1, 1])
  const copyRotateY = useTransform(scrollYProgress, [0, 0.34, 1], align === 'right' ? [-18, 0, 0] : [18, 0, 0])
  const copyFocus = useTransform(scrollYProgress, [0, 0.34, 1], ['blur(24px)', 'blur(0px)', 'blur(0px)'])

  return (
    <section className={`relative isolate bg-stone-950 ${overlapClass} ${layerClass}`} id={id} ref={ref}>
      <div className="sticky top-0 min-h-screen overflow-hidden">
        <motion.img
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
          decoding="async"
          height="864"
          loading="lazy"
          src={image}
          style={{ filter: imageFocus, scale: imageScale, x: imageX, y: imageY }}
          width="1536"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div
          className={
            align === 'right'
              ? 'absolute inset-0 bg-gradient-to-l from-stone-950 via-stone-950/70 to-stone-950/10'
              : 'absolute inset-0 bg-gradient-to-r from-stone-950 via-stone-950/70 to-stone-950/10'
          }
        />
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-stone-950 to-transparent" />
        <motion.div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-stone-950 to-transparent"
          style={{ opacity: wipeEdgeOpacity }}
        />
        <div
          className="relative z-10 mx-auto flex min-h-screen max-w-6xl items-center px-5 py-24 md:px-8"
          style={{ perspective: 1200 }}
        >
          <motion.div
            className={align === 'right' ? 'max-w-xl origin-right lg:ml-auto' : 'max-w-xl origin-left'}
            style={{ filter: copyFocus, opacity: copyOpacity, rotateY: copyRotateY, scale: copyScale, x: copyX }}
          >
            <h2 className="text-4xl font-semibold tracking-tight text-stone-50 md:text-6xl">{title}</h2>
            <p className="mt-6 text-lg leading-8 text-stone-200">{body}</p>
            <p className="mt-5 border-l border-amber-300 pl-5 text-base leading-7 text-stone-200">{note}</p>
          </motion.div>
        </div>
      </div>
      <div aria-hidden="true" className="h-screen" />
    </section>
  )
}
